import { Button, Css, Tooltip, useComputed, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ReactNode, useCallback } from "react";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { queryResult } from "src/utils";
import {
  SelectConfigurationStep_StructureFragment,
  useSelectConfigurationStep_SelectedConfigQuery,
} from "../1.SelectConfigurationStep.generated";
import { ApplyConfigurationFormInput } from "../applyConfigurationForm";
import { ApplyConfigurationCard } from "./ApplyConfigurationCard";
import { ApplyConfigurationCard_CheckoutConfigFragment } from "./ApplyConfigurationCard.generated";
import { NoConfigurationEmptyState } from "./NoConfigurationEmptyState";
import { SelectConfigurationModal } from "./SelectConfigurationModal";

type ConfigurationChangeSectionProps = {
  formState: ObjectState<ApplyConfigurationFormInput>;
  structure: SelectConfigurationStep_StructureFragment;
  currentConfig: ApplyConfigurationCard_CheckoutConfigFragment | undefined;
};

export function ConfigurationChangeSection(props: ConfigurationChangeSectionProps) {
  const { formState, structure, currentConfig } = props;
  const targetConfigId = useComputed(() => formState.targetConfigId.value, [formState]);
  const query = useSelectConfigurationStep_SelectedConfigQuery({
    variables: { configId: targetConfigId!, structureId: structure.id },
    skip: !targetConfigId,
  });
  if (!targetConfigId) {
    return <ConfigurationChangeSectionView formState={formState} structure={structure} currentConfig={currentConfig} />;
  } else {
    return queryResult(query, ({ checkoutConfig }) => (
      <ConfigurationChangeSectionView
        formState={formState}
        structure={structure}
        currentConfig={currentConfig}
        replacementConfig={checkoutConfig}
      />
    ));
  }
}

type ConfigurationChangeSectionViewProps = ConfigurationChangeSectionProps & {
  replacementConfig?: ApplyConfigurationCard_CheckoutConfigFragment;
};
function ConfigurationChangeSectionView(props: ConfigurationChangeSectionViewProps) {
  const { formState, structure, currentConfig, replacementConfig } = props;
  const { openModal, closeModal, setSize } = useModal();

  const chooseReplacement = useCallback(() => {
    setSize("xxl");
    openModal({
      size: "xxl",
      content: (
        <SelectConfigurationModal
          structure={structure}
          initialSelectedConfigId={replacementConfig?.id}
          onSelect={(config) => {
            const { productOffering, options } = config.config ?? {};
            formState.set({
              targetConfigId: config.id,
              readyPlanId: productOffering?.id,
              optionIds: options.map((o) => o.readyPlanOption.id),
              // A new configuration needs a new dry run, so the steps can't serve the previous one's preview
              dryRunChangeEventId: undefined,
            });
          }}
        />
      ),
    });
  }, [openModal, setSize, formState, structure, replacementConfig]);

  /**
   * A saved draft's config change can't be retargeted, so moving off its configuration deletes the draft on the next
   * save (see `useSaveApplyConfigurationChangeEvent`). Both ways of replacing it (the card and its trash icon) funnel
   * through here, so the user is warned once, before the saved configuration is first left.
   */
  function confirmReplacingSavedConfig(onConfirm: () => void) {
    if (!formState.id.value || formState.targetConfigId.dirty) return onConfirm();
    openModal({
      content: (
        <ConfirmationModal
          title="Replace configuration?"
          confirmationMessage="This will delete the existing configuration and replace it with the new one. Are you sure you want to continue?"
          label="Continue"
          danger
          // `onConfirm` may open the Choose Replacement modal in this one's place, so it closes the modal itself
          closeOnConfirm={false}
          onConfirmAction={onConfirm}
        />
      ),
      size: "md",
    });
  }

  if (!currentConfig && !replacementConfig) {
    return <NoConfigurationEmptyState onClick={chooseReplacement} />;
  } else {
    return (
      <div css={Css.df.gap3.mb3.$}>
        <ConfigurationPanel title="Original">
          {currentConfig ? (
            <ApplyConfigurationCard checkoutConfig={currentConfig} />
          ) : (
            <NoConfigurationEmptyState compact />
          )}
        </ConfigurationPanel>

        <ConfigurationPanel title="Replacement">
          {replacementConfig ? (
            <ApplyConfigurationCard
              checkoutConfig={replacementConfig}
              onClick={() => confirmReplacingSavedConfig(chooseReplacement)}
              onRemove={() =>
                confirmReplacingSavedConfig(() => {
                  // Dropping the replacement drops everything derived from it
                  formState.set({
                    targetConfigId: undefined,
                    readyPlanId: undefined,
                    optionIds: [],
                    dryRunChangeEventId: undefined,
                  });
                  closeModal();
                })
              }
            />
          ) : (
            <ChooseReplacementButton onClick={chooseReplacement} />
          )}
        </ConfigurationPanel>
      </div>
    );
  }
}

function ConfigurationPanel(props: { title: string; children: ReactNode }) {
  const { title, children } = props;
  return (
    <div css={Css.df.fdc.gap2.fb0.fg1.mw0.$}>
      <span css={Css.mdSb.$}>{title}</span>
      <div css={Css.df.fg1.$}>{children}</div>
    </div>
  );
}

function ChooseReplacementButton(props: { onClick: () => void }) {
  const { onClick } = props;
  return (
    <div css={Css.df.aic.jcc.w100.ba.bcGray300.br8.bgWhite.p3.$}>
      <Tooltip title="Select the configuration to apply to this structure">
        <Button label="Choose Replacement" variant="secondary" onClick={onClick} />
      </Tooltip>
    </div>
  );
}
